import React, { useMemo, useCallback } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CoreLink, Webform } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import {
  WysiwygField,
  PatternBlock,
  ButtonWrapper,
} from '../../../components';
import usePhaseState from '../../../helpers/usePhaseState';
import { AVO_PHASE_BEFORE, APP_OPTIONS, AVO_PHASE_DURING } from '../../../config/constants';
import type { AVOBlockCTA } from '../../../types/uncinc_profile/blocks';
import useCountdown from '../../../helpers/useCountdown';
import { timestampToDate, calculateDaysDifference } from '../../../helpers/timeUtils';

import './style.scss';

export interface AVOBlockCTAProps {
  entity: ImmutableEntity<AVOBlockCTA>;
}

/**
 * AppeltjesCTA Component
 *
 * Displays a call-to-action block for the Appeltjes van Oranje (AVO) initiative.
 * The content and behavior changes based on the current phase:
 * - BEFORE: Shows countdown in days until voting starts
 * - DURING: Shows live countdown until voting ends (updates every minute)
 * - AFTER: Shows standard content with action buttons
 */
const AppeltjesCTA: React.FC<AVOBlockCTAProps> = ({ entity }) => {
  const { t } = useTranslation();
  const block = entity.toJS();
  const title = block.info[0]?.value;
  const body = block.body[0]?.processed || '';
  const link = block.field_link[0];
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;
  const secondaryLink = block.field_link_secondary[0];
  const secondaryLinkTitle = secondaryLink?.title;
  const secondaryLinkUrl = secondaryLink?.alias || secondaryLink?.uri;

  const phase = usePhaseState();

  // Extract and memoize AVO dates from configuration
  const { startDate, endDate } = APP_OPTIONS.parameters.avoDates;
  const targetDate = useMemo(() => timestampToDate(startDate), [startDate]);
  const endTargetDate = useMemo(() => timestampToDate(endDate), [endDate]);

  // Use countdown hook for live updates during voting phase
  const { timeLeft, isExpired } = useCountdown(endTargetDate, {
    isActive: phase === AVO_PHASE_DURING,
  });

  // Memoize day text function to avoid recreating on every render
  const getDayText = useCallback((days: number): string => (
    days === 1 ? t('avo.countdown.days_single') : t('avo.countdown.days_plural')
  ), [t]);

  // Memoize title calculation based on phase
  const usableTitle = useMemo(() => {
    switch (phase) {
      case AVO_PHASE_BEFORE: {
        const daysUntilStart = calculateDaysDifference(targetDate);
        const dayText = getDayText(daysUntilStart);
        return title?.replace?.('[days]', `${daysUntilStart} ${dayText}`) || title;
      }
      case AVO_PHASE_DURING: {
        if (isExpired) {
          return t('avo.countdown.time_expired');
        }
        const { days, hours, minutes } = timeLeft;
        const dayText = getDayText(days);
        return t('avo.countdown.voting_countdown', {
          days,
          dayText,
          hours,
          minutes,
        });
      }
      default:
        return title;
    }
  }, [phase, title, targetDate, timeLeft, isExpired, getDayText, t]);

  // Memoize CSS classes for the actions section
  const actionBlockClasses = useMemo(() => classNames('AppeltjesCTABlock__actions', {
    'AppeltjesCTABlock__actions--webform': phase === AVO_PHASE_BEFORE,
  }), [phase]);

  // Memoize conditional content based on phase
  const conditionalContent = useMemo(() => {
    if (phase === AVO_PHASE_BEFORE) {
      return <Webform id="avo_voter_registration" />;
    }

    if (linkTitle) {
      return (
        <ButtonWrapper
          inverted
          href={linkUrl}
          className="AppeltjesCTABlock__actions__button"
        >
          {linkTitle}
        </ButtonWrapper>
      );
    }

    return null;
  }, [phase, linkTitle, linkUrl]);

  return (
    <div className="AppeltjesCTABlock">
      <PatternBlock className="AppeltjesCTABlock__pattern">
        <div className="AppeltjesCTABlock__title">
          <h3>{usableTitle} </h3>
        </div>
        <div className="AppeltjesCTABlock__subtitle">
          {body && (
            <WysiwygField
              property="schema:text"
              html={body}
            />
          )}
        </div>
        <div className={actionBlockClasses}>
          {conditionalContent}
          {secondaryLinkTitle && (
            <div className="AppeltjesCTABlock__link">
              <CoreLink href={secondaryLinkUrl}>
                {secondaryLinkTitle}
                <div className="icon icon-arrow-right icon-variant-4" />
              </CoreLink>
            </div>
          )}
        </div>
      </PatternBlock>
    </div>
  );
};

export default AppeltjesCTA;
